
import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { NursingHome } from "@/hooks/use-nursing-home";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormDescription,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";

interface FacilitiesFormProps {
  nursingHome: NursingHome;
  onSave: (data: Partial<NursingHome>) => Promise<NursingHome>;
  isSaving: boolean;
}

// Common facility options
const commonFacilities = [
  "Private Rooms",
  "Shared Rooms",
  "Gardens",
  "Dining Room", 
  "Lounge Area",
  "Activity Room",
  "Physical Therapy Room",
  "Chapel/Prayer Room",
  "Hair Salon",
  "Cinema Room",
  "Library",
  "Outdoor Seating",
  "Visiting Areas",
  "WiFi",
  "Elevator",
  "Wheelchair Access",
  "CCTV Security",
  "Parking",
];

// Common amenity options
const commonAmenities = [
  "WiFi Throughout",
  "TV in Rooms",
  "Phone in Rooms",
  "En-suite Bathrooms",
  "Personal Storage",
  "Garden Access",
  "Laundry Service",
  "Housekeeping",
  "Meal Choice/Menu",
  "Special Diet Accommodation",
  "Hairdressing",
  "Beauty Services",
  "Visiting Pet Program",
  "Resident Transport",
  "Guest Meals",
  "Overnight Guest Stay",
  "Newspaper Delivery",
];

// Common service options
const commonServices = [
  "24-Hour Nursing Care",
  "Dementia Care",
  "Respite Care",
  "Palliative Care",
  "Convalescent Care",
  "Post-Operative Care",
  "Physiotherapy",
  "Occupational Therapy",
  "Speech Therapy",
  "Dietician",
  "Podiatry",
  "Dental Services",
  "Optical Services",
  "Medication Management",
  "Social Activities",
  "Religious Services",
  "Exercise Programs",
];

const formSchema = z.object({
  facilities: z.array(z.string()).optional(),
  amenities: z.array(z.string()).optional(),
  services: z.array(z.string()).optional(),
  visiting_hours: z.string().optional(),
  customFacilities: z.string().optional(),
  customAmenities: z.string().optional(),
  customServices: z.string().optional(),
});

export function FacilitiesForm({ nursingHome, onSave, isSaving }: FacilitiesFormProps) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      facilities: nursingHome.facilities || [],
      amenities: nursingHome.amenities || [],
      services: nursingHome.services || [],
      visiting_hours: nursingHome.visiting_hours || '',
      customFacilities: '',
      customAmenities: '',
      customServices: '',
    },
  });

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    try {
      // Process custom items
      let facilities = [...(data.facilities || [])];
      let amenities = [...(data.amenities || [])];
      let services = [...(data.services || [])];
      
      // Add custom facilities if provided
      if (data.customFacilities) {
        const customItems = data.customFacilities
          .split(',')
          .map(item => item.trim())
          .filter(item => item.length > 0);
        facilities = [...facilities, ...customItems];
      }
      
      // Add custom amenities if provided
      if (data.customAmenities) {
        const customItems = data.customAmenities
          .split(',')
          .map(item => item.trim())
          .filter(item => item.length > 0);
        amenities = [...amenities, ...customItems];
      }
      
      // Add custom services if provided
      if (data.customServices) {
        const customItems = data.customServices
          .split(',')
          .map(item => item.trim())
          .filter(item => item.length > 0);
        services = [...services, ...customItems];
      }

      // Save data excluding the custom fields
      const { customFacilities, customAmenities, customServices, ...restData } = data;
      await onSave({
        ...restData,
        facilities,
        amenities,
        services,
      });
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="space-y-6">
          {/* Facilities Section */}
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-medium mb-4">Facilities</h3>
              
              <FormField
                control={form.control}
                name="facilities"
                render={() => (
                  <FormItem>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {commonFacilities.map((facility) => (
                        <FormField
                          key={facility}
                          control={form.control}
                          name="facilities"
                          render={({ field }) => {
                            return (
                              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(facility)}
                                    onCheckedChange={(checked) => {
                                      return checked
                                        ? field.onChange([...field.value || [], facility])
                                        : field.onChange(
                                            field.value?.filter(
                                              (value) => value !== facility
                                            )
                                          )
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  {facility}
                                </FormLabel>
                              </FormItem>
                            )
                          }}
                        />
                      ))}
                    </div>
                    <FormField
                      control={form.control}
                      name="customFacilities"
                      render={({ field }) => (
                        <FormItem className="mt-4">
                          <FormLabel>Additional Facilities</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. Outdoor Gym, Herb Garden (comma separated)" {...field} />
                          </FormControl>
                          <FormDescription>
                            Add custom facilities separated by commas
                          </FormDescription>
                        </FormItem>
                      )}
                    />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Amenities Section */}
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-medium mb-4">Amenities</h3>
              
              <FormField
                control={form.control}
                name="amenities"
                render={() => (
                  <FormItem>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {commonAmenities.map((amenity) => (
                        <FormField
                          key={amenity}
                          control={form.control}
                          name="amenities"
                          render={({ field }) => {
                            return (
                              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(amenity)}
                                    onCheckedChange={(checked) => {
                                      return checked
                                        ? field.onChange([...field.value || [], amenity])
                                        : field.onChange(
                                            field.value?.filter(
                                              (value) => value !== amenity
                                            )
                                          )
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  {amenity}
                                </FormLabel>
                              </FormItem>
                            )
                          }}
                        />
                      ))}
                    </div>
                    <FormField
                      control={form.control}
                      name="customAmenities"
                      render={({ field }) => (
                        <FormItem className="mt-4">
                          <FormLabel>Additional Amenities</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. Smart TVs, Customizable Room Decor (comma separated)" {...field} />
                          </FormControl>
                          <FormDescription>
                            Add custom amenities separated by commas
                          </FormDescription>
                        </FormItem>
                      )}
                    />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Services Section */}
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-medium mb-4">Services</h3>
              
              <FormField
                control={form.control}
                name="services"
                render={() => (
                  <FormItem>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {commonServices.map((service) => (
                        <FormField
                          key={service}
                          control={form.control}
                          name="services"
                          render={({ field }) => {
                            return (
                              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(service)}
                                    onCheckedChange={(checked) => {
                                      return checked
                                        ? field.onChange([...field.value || [], service])
                                        : field.onChange(
                                            field.value?.filter(
                                              (value) => value !== service
                                            )
                                          )
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  {service}
                                </FormLabel>
                              </FormItem>
                            )
                          }}
                        />
                      ))}
                    </div>
                    <FormField
                      control={form.control}
                      name="customServices"
                      render={({ field }) => (
                        <FormItem className="mt-4">
                          <FormLabel>Additional Services</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. Art Therapy, Music Therapy (comma separated)" {...field} />
                          </FormControl>
                          <FormDescription>
                            Add custom services separated by commas
                          </FormDescription>
                        </FormItem>
                      )}
                    />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Visiting Hours */}
          <Card>
            <CardContent className="pt-6">
              <FormField
                control={form.control}
                name="visiting_hours"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Visiting Hours</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. Monday-Sunday: 10am-8pm" {...field} />
                    </FormControl>
                    <FormDescription>
                      Specify the visiting hours policy for your nursing home
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
            <CardFooter className="border-t bg-muted/50 py-4">
              <Button type="submit" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </form>
    </Form>
  );
}
